import { buildProvidersListQuery } from '../lib';

// Response envelope is { data, correlationUUID } — the shared client
// unwraps `data` into `payload`. List payload: { items, continuationToken,
// totalCount } where totalCount is present on the FIRST page only and
// continuationToken is null on the last page.
export const listProvidersAction = ({ accountUuid, ...params }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/providers?${buildProvidersListQuery(params)}`,
});

// Wire values for the required set-default {context} route segment
// (backend change note 2026-08-12). A provider can be default for
// several contexts at once; each PUT sets one context and clears that
// context's previous holder server-side. `Surveys` surfaces in the UI
// as "Assessments" per client terminology.
export const PROVIDER_DEFAULT_CONTEXTS = {
  pdf: 'Pdf',
  claims: 'Claims',
  surveys: 'Surveys',
};

// The list payload reports each context's default on its own boolean.
// There is no combined "isDefault" field — anything reading a default
// must name the context it means.
export const PROVIDER_DEFAULT_FLAGS = {
  [PROVIDER_DEFAULT_CONTEXTS.pdf]: 'isDefaultPdf',
  [PROVIDER_DEFAULT_CONTEXTS.claims]: 'isDefaultClaims',
  [PROVIDER_DEFAULT_CONTEXTS.surveys]: 'isDefaultSurveys',
};

// No body. 404 means the row went stale (provider removed) OR the
// context segment is missing (routing-layer 404) — callers refresh the
// list instead of surfacing the raw error.
export const setDefaultProviderAction = ({ accountUuid, providerUuid, context }) => ({
  method: 'PUT',
  endpoint: `/accounts/${accountUuid}/providers/${providerUuid}/default/${context}`,
});

// Clears one context's default without handing it to another provider,
// leaving that context with no default at all. Same {context} values as
// setDefaultProviderAction. No body; the 200 carries an empty envelope.
// Idempotent server-side — clearing an already-cleared context still
// returns 200. 400 = bad/missing context, 404 = stale row.
export const unsetDefaultProviderAction = ({ accountUuid, providerUuid, context }) => ({
  method: 'DELETE',
  endpoint: `/accounts/${accountUuid}/providers/${providerUuid}/default/${context}`,
});

// Creates one provider row. Body: { name, providerTitle?,
// providerNPINumber? } — same 64/64/10-digit rules as the PATCH route.
// `linkedUserUUID` is accepted by the API but deliberately not sent
// from here: linking happens when a user is given the provider role in
// user settings, and this screen only displays the resulting isLinked
// flag. The 201-equivalent 200 payload omits isLinked and the default
// flags, so callers re-fetch the list rather than merging the response.
// 409 = another live provider on the account already holds this name
// (case/whitespace-insensitive) — surfaced inline on the name field,
// never as a generic failure toast. 422 = field validation.
export const addProviderAction = ({ accountUuid, provider }) => ({
  method: 'POST',
  endpoint: `/accounts/${accountUuid}/providers`,
  body: provider,
});

// Body is an RFC 6902 JSON Patch array over /name, /providerTitle and
// /providerNPINumber — only ops for fields that actually changed; the
// caller owns the diff. The endpoint requires the JSON Patch media type
// (the request interceptor only defaults Content-Type when the action
// does not set one). The 200 payload carries just
// { uuid, name, providerTitle, providerNPINumber } — no isDefault /
// isLinked — so consumers re-fetch the list instead of merging the row.
// 404 = stale row, same handling as setDefaultProviderAction.
export const editProviderAction = ({ accountUuid, providerUuid, patches }) => ({
  method: 'PATCH',
  endpoint: `/accounts/${accountUuid}/providers/${providerUuid}`,
  body: patches,
  headers: { 'Content-Type': 'application/json-patch+json' },
});

// The standard credentials a provider may carry, in display order:
// `["MD", "DO", "APRN", "PA"]`. Same permission as the providers list
// (ACCOUNT_USERS_GET). The list does not vary by account — the
// accountUUID is in the path only because it carries the permission —
// so consumers fetch it once per session (see useProviderTitles).
//
// Adding a credential later is a backend change with no dashboard
// release, which is the whole reason this is an endpoint and not a
// constant.
export const listProviderTitlesAction = ({ accountUuid }) => ({
  method: 'GET',
  endpoint: `/accounts/${accountUuid}/providers/titles`,
});
